import type {SchematicHierarchyGroup} from '../components/diff-list/schematic-hierarchy.ts';

export const FILE_DIFF_STATUS = {
  ADDED: 'added',
  DELETED: 'deleted',
  MODIFIED: 'modified',
  RENAMED: 'renamed',
  COPIED: 'copied',
  TYPECHANGED: 'typechanged',
  UNCHANGED: 'unchanged',
} as const;
export type FileDiffStatus = typeof FILE_DIFF_STATUS[keyof typeof FILE_DIFF_STATUS];

export const FILE_VIEWED_STATUS = {
  UNVIEWED: 0,
  HAS_CHANGED: 1,
  VIEWED: 2,
} as const;
export type FileViewedStatus = typeof FILE_VIEWED_STATUS[keyof typeof FILE_VIEWED_STATUS];

export const LOADING_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  SUCCESS: 'success',
  FAILED: 'failed',
} as const;
type LoadingStatus = typeof LOADING_STATUS[keyof typeof LOADING_STATUS];

export const FILE_TYPE = {
  PROJECT_ROOT: 'project-root',
  DIRECTORY: 'dir',
  SUBMODULE: 'submodule-diff',
  SYMLINK: 'symlink-diff',
  MULTIPAGE: 'multi-page-diff',
  PCB: 'pcb-diff',
  SCHEMATIC: 'sch-diff',
  PCB_LIBRARY: 'pcblib-diff',
  SCHEMATIC_LIBRARY: 'schlib-diff',
  PROJECT: 'project-diff',
  PROJECT_PAGE: 'proj-page-diff',
  PDF: 'pdf-diff',
  FILE: 'file-diff', // non-ECAD file
} as const;
export type FileType = typeof FILE_TYPE[keyof typeof FILE_TYPE];

export const MULTIPAGE_FILE_TYPES = [FILE_TYPE.MULTIPAGE, FILE_TYPE.PROJECT];
export type MultiPageFileType = typeof MULTIPAGE_FILE_TYPES[number];

export function isMultipage(ty: FileType): ty is MultiPageFileType {
  return (MULTIPAGE_FILE_TYPES as FileType[]).includes(ty);
}

// File types whose diff can be rendered by Crysknife. Non-ECAD entries
// (FILE, PDF, SUBMODULE, SYMLINK, …) must NOT hit the diff_preview route —
// it has no extension gate on the backend and enqueues a crysknife
// convert-file job that fails with "Unrecognized file format".
export const ECAD_DIFFABLE_FILE_TYPES = [
  FILE_TYPE.SCHEMATIC,
  FILE_TYPE.PCB,
  FILE_TYPE.MULTIPAGE,
  FILE_TYPE.PROJECT,
  FILE_TYPE.PROJECT_PAGE,
];
export type EcadDiffableFileType = typeof ECAD_DIFFABLE_FILE_TYPES[number];

export function isEcadDiffable(ty: FileType): ty is EcadDiffableFileType {
  return (ECAD_DIFFABLE_FILE_TYPES as FileType[]).includes(ty);
}

export type ProjectTreeNode = {
  /** What type of document this is */
  ty: FileType;
  /** The filename of the document */
  name: string;
  /** An ID for this particular document */
  nameHash: string;
  /** The full path to the document */
  headPath: string;
  /** The full path to the document in the base commit, only present if different from headPath */
  basePath?: string;
  /** Any children to represent under the document */
  children: ProjectTreeNode[];

  /** This document's parent document, if this is a page in a multipage document */
  parentDocName?: string;
  /** This document's page ID, if this is a page in a multipage document */
  headPageId?: string;
  /** This document's page ID in the base commit, if it changed and this is a page in a multipage document */
  basePageId?: string;

  /** The diff status of the document (if this is a diff) */
  status: FileDiffStatus;
  /** Whether this document has been checked as viewed for this user in this DR */
  // TODO: I feel like storing this on this structure feels weird
  isViewed?: FileViewedStatus;

  /** Whether the children of this document are currently being loaded */
  loadingChildrenStatus: LoadingStatus;
};

/* *** Everything below this should be considered legacy. *** */

type BaseTreeNode = {
  ty: string;
  name: string;
  headPath: string;
  basePath?: string;
};

export type FileTreeDirectory = BaseTreeNode & {
  ty: 'dir';
  children: TreeNode[];
};

export type FileTreeFile = BaseTreeNode & {
  nameHash: string;
  status: FileDiffStatus;
  isViewed?: boolean;
};

export type SubmoduleDiff = FileTreeFile & {
  ty: 'submodule-diff';
};

export type SymlinkDiff = FileTreeFile & {
  ty: 'symlink-diff';
};

export type FileDiff = FileTreeFile & {
  ty: 'file-diff';
};

export type PdfDiff = FileTreeFile & {
  ty: 'pdf-diff';
};

export type SchDiff = FileTreeFile & {
  ty: 'sch-diff';
};

export type PcbDiff = FileTreeFile & {
  ty: 'pcb-diff';
};

export type PcbLibDiff = FileTreeFile & {
  ty: 'pcblib-diff';
};

export type SchLibDiff = FileTreeFile & {
  ty: 'schlib-diff';
};

export type ProjectPageDiff = FileTreeFile & {
  ty: 'proj-page-diff';
  pageId: string;
  basePageId: string;
  projectName: string;
  pageViewedPath: string;
};

export type MultiPageFileTreeFile = FileTreeFile & {
  loadingStatus?: LoadingStatus;
  children?: ProjectPageDiff[];
  hierarchy?: SchematicHierarchyGroup[];
};

export type ProjectDiff = MultiPageFileTreeFile & {
  ty: 'proj-diff';
};

export type MultiPageDiff = MultiPageFileTreeFile & {
  ty: 'multi-page-diff';
};

export type TreeNode = FileTreeDirectory | SubmoduleDiff | SymlinkDiff | FileDiff | SchDiff | SchLibDiff | PcbDiff | PdfDiff | PcbLibDiff | ProjectPageDiff | ProjectDiff | MultiPageDiff;
export type PreviewableTreeNode = SubmoduleDiff | SymlinkDiff | FileDiff | SchDiff | PcbDiff | ProjectPageDiff | ProjectDiff | MultiPageDiff;
export type LoadableTreeNode = ProjectDiff | MultiPageDiff;
